/**
 * Gestione centralizzata dello stato di autenticazione
 * Risolve il problema della race condition tra storage del token e query di autenticazione
 */

import { queryClient } from './queryClient';
import { getToken, saveToken, removeToken } from './token-storage';

// Stato interno per tracciare lo stato dell'autenticazione
let authState = {
  isInitialized: false,
  hasToken: false,
  isAuthenticating: false
};

// Event emitter per notificare i cambiamenti di stato
const authStateListeners = new Set<(result: any) => void>();

export function subscribeToAuthState(listener: (result: any) => void) {
  authStateListeners.add(listener);
  return () => authStateListeners.delete(listener);
}

function notifyAuthStateChange() {
  authStateListeners.forEach(listener => listener({}));
}

/**
 * Inizializza lo stato di autenticazione al caricamento dell'app
 */
export function initializeAuthState() {
  if (authState.isInitialized) return;
  
  console.log('[AUTH STATE] Inizializzazione stato autenticazione...');
  
  try {
    const token = getToken();
    authState.hasToken = !!token;
    authState.isInitialized = true;
    
    console.log('[AUTH STATE] Stato inizializzato:', {
      hasToken: authState.hasToken,
      tokenLength: token ? token.length : 0
    });
    
    notifyAuthStateChange();
  } catch (error) {
    console.error('[AUTH STATE] Errore durante inizializzazione:', error);
    authState.isInitialized = true;
    authState.hasToken = false;
    notifyAuthStateChange();
  }
}

/**
 * Aggiorna lo stato quando un nuovo token viene salvato
 */
export function updateAuthStateOnLogin(token: string) {
  console.log('[AUTH STATE] Aggiornamento stato dopo login...');
  
  // Salva il token
  saveToken(token);
  
  // Aggiorna lo stato interno
  authState.hasToken = true;
  authState.isAuthenticating = false;
  
  // Notifica il cambiamento
  notifyAuthStateChange();
  
  // Forza il refetch della query di autenticazione
  queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });
  
  console.log('[AUTH STATE] Stato aggiornato dopo login');
}

/**
 * Aggiorna lo stato quando l'utente fa logout
 */
export function updateAuthStateOnLogout() {
  console.log('[AUTH STATE] Aggiornamento stato dopo logout...');
  
  // Rimuovi il token
  removeToken();
  
  // Aggiorna lo stato interno
  authState.hasToken = false;
  authState.isAuthenticating = false;
  
  // Notifica il cambiamento
  notifyAuthStateChange();
  
  // Pulisci la cache
  queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });
  
  console.log('[AUTH STATE] Stato aggiornato dopo logout');
}

/**
 * Indica se l'app è pronta per effettuare query di autenticazione
 */
export function isAuthStateReady(): boolean {
  return authState.isInitialized;
}

/**
 * Indica se c'è un token disponibile
 */
export function hasAuthToken(): boolean {
  return authState.hasToken;
}

/**
 * Ottiene lo stato corrente dell'autenticazione
 */
export function getAuthState() {
  return { ...authState };
}

/**
 * Segna l'inizio di un processo di autenticazione
 */
export function setAuthenticating(isAuthenticating: boolean) {
  authState.isAuthenticating = isAuthenticating;
  notifyAuthStateChange();
}